import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { EntityHelper } from "@/common/helpers/entity";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useDisplayProps } from "@/common/hooks/useDisplayProps";
import { RepairOperationDtoAll } from "@/common/mapping/mappers/repairOperation";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { apiClient } from "@/core/api/ApiClient";
import {
  RepairOperationDto,
  RepairOperationItemDto,
  RepairOperationReferenceDto,
} from "@/core/api/generated/v1";

import MenuWithTrigger from "../../Menu/MenuWithTrigger";
import { getContractInlineTitleShort } from "../Contract/ContractInline";
import { getVehicleInlineTitleShort } from "../Vehicle/VehicleInline";
import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "../components/EntityInline/BaseEntityInline";
import { EntityInlineActionIcon } from "../components/EntityInline/EntityInlineActionIcon";
import { EntityInlineActionIconButton } from "../components/EntityInline/EntityInlineActionIconButton";
import { EntityInlineActions } from "../components/EntityInline/EntityInlineActions";
import { RepairOperationViewModal } from "./View/RepairOperationViewModal";

const defaultDisplayProps = { vehicle: true, contract: true };
type DisplayProps = typeof defaultDisplayProps;

export const getRepairOperationInlineTitle = (
  data: Nil<RepairOperationDtoAll>,
  displayProps?: DisplayProps,
) =>
  data
    ? StringHelper.join(
        [
          EntityHelper.getNumberTitle(data),
          enumService.getEnumValueName(ApiEnumName.RepairOperationStage, data.stageHistory?.stage),
          displayProps?.vehicle && data.vehicle
            ? getVehicleInlineTitleShort(data.vehicle)
            : undefined,
          displayProps?.contract && data.contract
            ? getContractInlineTitleShort(data.contract)
            : undefined,
        ],
        ", ",
        {
          skipEmpty: true,
        },
      )
    : undefined;

export interface RepairOperationInlineProps extends BaseEntityInlineInheritableProps {
  entity: RepairOperationDto | RepairOperationReferenceDto | null | undefined;
  entityId?: string | null;
  item?: RepairOperationItemDto;
  itemId?: string | null;
  displayProps?: Partial<DisplayProps>;
}

export default function RepairOperationInline({
  entity,
  entityId,
  item,
  itemId,
  displayProps,
  fetch = true,
  withActions,
  withMenu,
  viewProps,
  ...otherProps
}: RepairOperationInlineProps) {
  const displayPropsComputed = useDisplayProps([defaultDisplayProps, displayProps]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.repairOperationsApi.apiV1RepairOperationsRepairOperationIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      repairOperationId: entityId!,
    },
    {
      deps: [entityId],
      skip: !fetch || !entityId || !!entity,
    },
  );
  entity = request?.data || entity;

  const entityIdComputed = entity?.id || entityId;
  const full = entity as RepairOperationDto;
  // const ref = entity as RepairOperationReferenceDto;

  item = item || (itemId ? full?.items?.find((x) => x.id === itemId) : undefined);

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        request={request}
        icon={<AppIcon of='repairOperation' inText />}
        content={
          <>
            {getRepairOperationInlineTitle(entity, displayPropsComputed)}
            {item && <> (#{item.localNumber})</>}
            {!entity && !item && <>Repair operation</>}
          </>
        }
        withActions={withActions}
        withMenu={withMenu}
        actions={
          !withActions ? undefined : (
            <EntityInlineActions>
              <EntityInlineActionIconButton
                onClick={() => setIsViewModalOpen(true)}
                tooltipProps={{ title: "Quick view" }}
              >
                <EntityInlineActionIcon of='quickView' />
              </EntityInlineActionIconButton>
            </EntityInlineActions>
          )
        }
        menu={
          !withMenu ? undefined : (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <EntityInlineActionIconButton>
                  <EntityInlineActionIcon of='moreVert' />
                </EntityInlineActionIconButton>
              }
            >
              <MenuItem dense onClick={() => setIsViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view</ListItemText>
              </MenuItem>
            </MenuWithTrigger>
          )
        }
        {...otherProps}
        tooltipTitle={otherProps?.tooltipTitle || "Repair operation"}
      />

      {(withActions || withMenu) && (
        <>
          {/* View modal */}
          {entityIdComputed && (
            <RepairOperationViewModal
              open={isViewModalOpen}
              onClose={() => setIsViewModalOpen(false)}
              viewProps={{
                repairOperationId: entityIdComputed,
                repairOperation: EntityHelper.nullIfNotFull(entity),
                onUpdate: viewProps?.onUpdate || null,
                onDelete: viewProps?.onDelete || null,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
